import React, { useCallback } from 'react';
import { Container, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ClaimsProvider, useClaims } from '../../contexts/ClaimsContext';
import useSnackbar from '../../hooks/useSnackbar';
import UB04FormComponent from '../../components/UB04Form/UB04Form';
import PDFViewer from '../../components/PDFViewer/PDFViewer';
import SearchClaims from '../../components/UB04Form/SearchClaims';

// Import custom hooks
import useUB04FormManagement from './hooks/useUB04FormManagement';
import useUnsavedChanges from './hooks/useUnsavedChanges';
import useViewMode from './hooks/useViewMode';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';

// Import components
import FormTabs from './components/FormTabs';
import ViewModeToggle from './components/ViewModeToggle';
import UnsavedChangesDialog from './components/UnsavedChangesDialog';
import NoFormsPlaceholder from './components/NoFormsPlaceholder';
import PageHeader from './components/PageHeader';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  formContainer: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * UB04 Manual Ingest page component
 */
const UB04IngestPageInner: React.FC = () => {
  const classes = useStyles();
  const showSnackbar = useSnackbar();
  const { selectedClaimId } = useClaims();

  // Initialize hooks
  const { viewMode, setMode } = useViewMode('form');
  
  const {
    getCurrentForm,
    getCurrentForms,
    getCurrentFormIndex,
    changeFormIndex,
    handleFormChange,
    addNewForm,
    selectClaim,
    saveCurrentForm,
    handleProceedWithAction,
  } = useUB04FormManagement({ showSnackbar, selectedClaimId });

  const {
    unsavedChanges,
    setHasUnsavedChanges,
    showUnsavedDialog,
    handlePotentiallyBlockedAction,
    handleDialogAction,
    cancelPendingAction,
  } = useUnsavedChanges();

  // Get current form and forms
  const currentForm = getCurrentForm();
  const currentForms = getCurrentForms();
  const currentFormIndex = getCurrentFormIndex();

  // Set up keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'b',
      ctrlKey: true,
      handler: () => {
        // Only toggle if we have a form with a PDF
        if (currentForm && currentForm.fileUrl) {
          setMode(viewMode === 'form' ? 'pdf' : 'form');
        }
      },
    },
  ]);

  // Handle form tab change
  const handleFormTabChange = useCallback(
    (event: React.ChangeEvent<unknown>, newValue: number) => {
      const action = {
        type: 'changeFormTab',
        payload: { index: newValue },
      };
      if (handlePotentiallyBlockedAction(action)) {
        changeFormIndex(newValue);
      }
    },
    [handlePotentiallyBlockedAction, changeFormIndex]
  );

  // Handle adding a new form
  const handleAddForm = useCallback(() => {
    const action = { type: 'addForm' };
    if (handlePotentiallyBlockedAction(action)) {
      addNewForm();
    }
  }, [handlePotentiallyBlockedAction, addNewForm]);

  // Handle selecting a UB04 claim from search
  const handleSelectClaim = useCallback(
    (selectedForm) => {
      const action = { type: 'selectClaim', payload: [selectedForm] };
      if (handlePotentiallyBlockedAction(action)) {
        selectClaim(selectedForm);
        setHasUnsavedChanges(false);
      }
    },
    [handlePotentiallyBlockedAction, selectClaim, setHasUnsavedChanges]
  );

  // Handle form changes
  const handleFormChangeWrapper = useCallback(
    (updatedForm) => {
      const changed = handleFormChange(updatedForm);
      if (changed) {
        setHasUnsavedChanges(true);
      }
    },
    [handleFormChange, setHasUnsavedChanges]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      await saveCurrentForm();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving UB04 form:', error);
    }
  }, [saveCurrentForm, setHasUnsavedChanges]);

  // Handle dialog actions
  const handleSaveDialog = useCallback(() => {
    handleDialogAction(
      true,
      saveCurrentForm,
      handleProceedWithAction
    );
  }, [handleDialogAction, saveCurrentForm, handleProceedWithAction]);

  const handleDiscardDialog = useCallback(() => {
    handleDialogAction(
      false,
      saveCurrentForm,
      handleProceedWithAction
    );
  }, [handleDialogAction, saveCurrentForm, handleProceedWithAction]);

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (mode: 'form' | 'pdf') => {
      setMode(mode);
    },
    [setMode]
  );

  return (
    <Container maxWidth="xl" className={classes.root}>
      <PageHeader
        formType="ub04"
        hasUnsavedChanges={unsavedChanges}
        hasForms={currentForms.length > 0}
        onSave={handleSave}
        onAddNewForm={handleAddForm}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <SearchClaims onSelectClaim={handleSelectClaim} />
        </Grid>

        <Grid item xs={12} md={9}>
          {currentForms.length > 0 ? (
            <>
              <FormTabs
                forms={currentForms}
                currentIndex={currentFormIndex}
                onTabChange={handleFormTabChange}
              />

              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                pdfAvailable={!!currentForm?.fileUrl}
              />

              <div className={classes.formContainer}>
                {viewMode === 'form' ? (
                  currentForm && (
                    <UB04FormComponent
                      formData={currentForm as any}
                      onFormChange={handleFormChangeWrapper}
                      onSave={handleSave}
                    />
                  )
                ) : currentForm?.fileUrl ? (
                  <PDFViewer pdfUrl={currentForm.fileUrl} />
                ) : (
                  <NoFormsPlaceholder onAddNewForm={handleAddForm} />
                )}
              </div>
            </>
          ) : (
            <NoFormsPlaceholder onAddNewForm={handleAddForm} />
          )}
        </Grid>
      </Grid>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onSave={handleSaveDialog}
        onDiscard={handleDiscardDialog}
        onCancel={cancelPendingAction}
      />
    </Container>
  );
};

/**
 * Wrapper component that provides the ClaimsContext
 */
export const UB04IngestPage: React.FC = () => {
  return (
    <ClaimsProvider>
      <UB04IngestPageInner />
    </ClaimsProvider>
  );
};
